import { createRequestActionCreators, RequestAction, createPayloadAction, createAction } from 'helpers/actionsHelpers'
import {
  FeedUserUid,
  CommentUid,
  PostUid,
  Post,
  FeedFilterQuery,
  CountData,
  Comment,
  ComposeId,
  FeedUser,
  SelectableFeedFilter,
  FollowedPostsCount,
  FeedFilterStatus,
  WithPostUid,
  SocialProfileState,
  FeedOrdering,
  FeedTag,
  FeedTagName,
  CommentsThread,
  WithCommentUid,
  WithComposeId,
  TagsOrdering,
  TagCollection,
} from 'interfaces/feed'
import { CursorPaginatable, PaginationLinks } from 'interfaces/table'
import { PayloadAction, SimpleAction } from 'interfaces/action'
import { AnyObj, Collection } from 'interfaces'
import { Peergroup, PeergroupRelation } from 'interfaces/peergroups'
import { UserImage } from 'interfaces/userImage'
import { InsightId } from 'interfaces/insights'

// ===========================================================================//
// Users                                                                      //
// ===========================================================================//

type GetFeedMemberInfoResponse = {
  member: FeedUser
  avatar: UserImage | null
}
export const GET_FEED_MEMBER_INFO = 'GET_FEED_MEMBER_INFO' as const
type GetFeedMemberInfo = RequestAction<typeof GET_FEED_MEMBER_INFO, AnyObj, GetFeedMemberInfoResponse>
export const getFeedMemberInfoActionCreator = createRequestActionCreators<typeof GET_FEED_MEMBER_INFO, AnyObj, GetFeedMemberInfoResponse>(GET_FEED_MEMBER_INFO)

type GetFeedAuthorPayload = {
  username: string
}
export const GET_FEED_AUTHOR = 'GET_FEED_AUTHOR' as const
type GetFeedAuthor = RequestAction<typeof GET_FEED_AUTHOR, GetFeedAuthorPayload, FeedUser>
export const getFeedAuthorActionCreator = createRequestActionCreators<typeof GET_FEED_AUTHOR, GetFeedAuthorPayload, AnyObj>(GET_FEED_AUTHOR)

type ToggleAuthorFollowingPayload = {
  uid: FeedUserUid
  toFollow: boolean
}
export const TOGGLE_AUTHOR_FOLLOWING = 'TOGGLE_AUTHOR_FOLLOWING' as const
export type ToggleAuthorFollowing = RequestAction<typeof TOGGLE_AUTHOR_FOLLOWING, ToggleAuthorFollowingPayload, AnyObj>
export const toggleAuthorFollowingActionCreator =
  createRequestActionCreators<typeof TOGGLE_AUTHOR_FOLLOWING, ToggleAuthorFollowingPayload, AnyObj>(TOGGLE_AUTHOR_FOLLOWING)

type GetFollowedUsersActionPayload = {
  links: PaginationLinks | null
}
export type NormalizedFollowedUsersResponse = {
  users: Collection<FeedUser, 'uid'>
  links: PaginationLinks
  avatars: Collection<UserImage, 'id'>
}
export const GET_FOLLOWED_USERS = 'GET_FOLLOWED_USERS' as const
export type GetFollowedUsers = RequestAction<typeof GET_FOLLOWED_USERS, GetFollowedUsersActionPayload, NormalizedFollowedUsersResponse>
export const getFollowedUsersActionCreator = createRequestActionCreators<
  typeof GET_FOLLOWED_USERS,
  GetFollowedUsersActionPayload,
  NormalizedFollowedUsersResponse
>(GET_FOLLOWED_USERS)

export const RESET_FOLLOWED_USERS = 'RESET_FOLLOWED_USERS' as const
export type ResetFollowedUsers = SimpleAction<typeof RESET_FOLLOWED_USERS>
export const resetFollowedUsersActionCreator = () => createAction(RESET_FOLLOWED_USERS)

export const SCHEDULE_RESET_FOLLOWED_USERS = 'SCHEDULE_RESET_FOLLOWED_USERS' as const
export type ScheduleResetFollowedUsers = SimpleAction<typeof SCHEDULE_RESET_FOLLOWED_USERS>
export const scheduleResetFollowedUsersActionCreator = () => createAction(SCHEDULE_RESET_FOLLOWED_USERS)

// ===========================================================================//
// Tags                                                                       //
// ===========================================================================//

type GetFeedTagPayload = {
  tagName: string
}
export const GET_FEED_TAG = 'GET_FEED_TAG' as const
type GetFeedTag = RequestAction<typeof GET_FEED_TAG, GetFeedTagPayload, FeedTag>
export const getFeedTagActionCreator = createRequestActionCreators<typeof GET_FEED_TAG, GetFeedTagPayload, AnyObj>(GET_FEED_TAG)

export type GetFeedTagsRequest = {
  search?: string
  ordering?: TagsOrdering
  limit: number
}
export type GetFeedTagsResponse = {
  payload: TagCollection
}
export const GET_FEED_TAGS = 'GET_FEED_TAGS' as const
type GetFeedTags = RequestAction<typeof GET_FEED_TAGS, GetFeedTagsRequest, GetFeedTagsResponse>
export const getFeedTagsActionCreator = createRequestActionCreators<typeof GET_FEED_TAGS, GetFeedTagsRequest, GetFeedTagsResponse>(GET_FEED_TAGS)

type ToggleTagFollowingPayload = {
  tag_name: FeedTagName
  toFollow: boolean
}
export const TOGGLE_TAG_FOLLOWING = 'TOGGLE_TAG_FOLLOWING' as const
export type ToggleTagFollowing = RequestAction<typeof TOGGLE_TAG_FOLLOWING, ToggleTagFollowingPayload, AnyObj>
export const toggleTagFollowingActionCreator =
  createRequestActionCreators<typeof TOGGLE_TAG_FOLLOWING, ToggleTagFollowingPayload, AnyObj>(TOGGLE_TAG_FOLLOWING)

type ToggleCommentLikePayload =
  & WithCommentUid
  & {
    toLike: boolean
    authorUid: FeedUserUid
  }
export const TOGGLE_COMMENT_LIKE = 'TOGGLE_COMMENT_LIKE' as const
export type ToggleCommentLike = RequestAction<typeof TOGGLE_COMMENT_LIKE, ToggleCommentLikePayload, CountData>
export const toggleCommentLikeActionCreator =
  createRequestActionCreators<typeof TOGGLE_COMMENT_LIKE, ToggleCommentLikePayload, CountData>(TOGGLE_COMMENT_LIKE)

type GetFollowedTagsActionPayload = {
  links: PaginationLinks | null
}
export type NormalizedFollowedTagsResponse = {
  tags: TagCollection
  links: PaginationLinks
}
export const GET_FOLLOWED_TAGS = 'GET_FOLLOWED_TAGS' as const
export type GetFollowedTags = RequestAction<typeof GET_FOLLOWED_TAGS, GetFollowedTagsActionPayload, NormalizedFollowedTagsResponse>
export const getFollowedTagsActionCreator = createRequestActionCreators<
  typeof GET_FOLLOWED_TAGS,
  GetFollowedTagsActionPayload,
  NormalizedFollowedTagsResponse
>(GET_FOLLOWED_TAGS)

export const RESET_FOLLOWED_TAGS = 'RESET_FOLLOWED_TAGS' as const
export type ResetFollowedTags = SimpleAction<typeof RESET_FOLLOWED_TAGS>
export const resetFollowedTagsActionCreator = () => createAction(RESET_FOLLOWED_TAGS)

export const SCHEDULE_RESET_FOLLOWED_TAGS = 'SCHEDULE_RESET_FOLLOWED_TAGS' as const
export type ScheduleResetFollowedTags = SimpleAction<typeof SCHEDULE_RESET_FOLLOWED_TAGS>
export const scheduleResetFollowedTagsActionCreator = () => createAction(SCHEDULE_RESET_FOLLOWED_TAGS)

// ===========================================================================//
// Posts                                                                      //
// ===========================================================================//

export type NormalizedPostsResponse = {
  users: Collection<FeedUser, 'uid'>
  comments: Collection<Comment, 'uid'>
  posts: Collection<Post, 'uid'>
  postsByFilter: CursorPaginatable<PostUid>
  images: Collection<UserImage, 'id'>
}

type GetPostsPayload = {
  query: FeedFilterQuery
  ordering?: FeedOrdering
  feedPostUids: CursorPaginatable<PostUid> | null
}
export const GET_POSTS = 'GET_POSTS' as const
export type GetPosts = RequestAction<typeof GET_POSTS, GetPostsPayload, NormalizedPostsResponse>
export const getPostsActionCreator =
  createRequestActionCreators<typeof GET_POSTS, GetPostsPayload, NormalizedPostsResponse>(GET_POSTS)

export const RESET_POSTS = 'RESET_POSTS' as const
export type ResetPosts = SimpleAction<typeof RESET_POSTS>
export const resetPostsActionCreator = () => createAction(RESET_POSTS)

type ReportPostPayload = WithPostUid
export const REPORT_POST = 'REPORT_POST' as const
type ReportPost = RequestAction<typeof REPORT_POST, ReportPostPayload, AnyObj>
export const reportPostActionCreator =
  createRequestActionCreators<typeof REPORT_POST, ReportPostPayload, AnyObj>(REPORT_POST)

type ReportInsightPayload =
  & WithPostUid
  & {
    insightId: InsightId
  }
export const REPORT_INSIGHT = 'REPORT_INSIGHT' as const
type ReportInsight = RequestAction<typeof REPORT_INSIGHT, ReportInsightPayload, AnyObj>
export const reportInsightActionCreator =
  createRequestActionCreators<typeof REPORT_INSIGHT, ReportInsightPayload, AnyObj>(REPORT_INSIGHT)

type DeletePostPayload = WithPostUid
export const DELETE_POST = 'DELETE_POST' as const
type DeletePost = RequestAction<typeof DELETE_POST, DeletePostPayload, AnyObj>
export const deletePostActionCreator =
  createRequestActionCreators<typeof DELETE_POST, DeletePostPayload, AnyObj>(DELETE_POST)

type TogglePostFollowingPayload =
  & WithPostUid
  & {
    toFollow: boolean
  }
export const TOGGLE_POST_FOLLOWING = 'TOGGLE_POST_FOLLOWING' as const
export type TogglePostFollowing = RequestAction<typeof TOGGLE_POST_FOLLOWING, TogglePostFollowingPayload, CountData>
export const togglePostFollowingActionCreator =
  createRequestActionCreators<typeof TOGGLE_POST_FOLLOWING, TogglePostFollowingPayload, AnyObj>(TOGGLE_POST_FOLLOWING)

type TogglePostLikePayload =
  & WithPostUid
  & {
    toLike: boolean
    authorUid: FeedUserUid
  }
export const TOGGLE_POST_LIKE = 'TOGGLE_POST_LIKE' as const
export type TogglePostLike = RequestAction<typeof TOGGLE_POST_LIKE, TogglePostLikePayload, CountData>
export const togglePostLikeActionCreator =
  createRequestActionCreators<typeof TOGGLE_POST_LIKE, TogglePostLikePayload, CountData>(TOGGLE_POST_LIKE)

type ToggleEditPostPayload =
  & WithPostUid
  & {
    isEditing: boolean
  }
export const TOGGLE_EDIT_POST = 'TOGGLE_EDIT_POST' as const
type ToggleEditPost = PayloadAction<typeof TOGGLE_EDIT_POST, ToggleEditPostPayload>
export const toggleEditPostActionCreator = (payload: ToggleEditPostPayload) =>
  createPayloadAction(TOGGLE_EDIT_POST, payload)

export const LOAD_FOLLOWED_POSTS_COUNT = 'LOAD_FOLLOWED_POSTS_COUNT' as const
export type LoadFollowedPostsCount = RequestAction<typeof LOAD_FOLLOWED_POSTS_COUNT, AnyObj, FollowedPostsCount>
export const loadFollowedPostsCountActionCreator =
  createRequestActionCreators<typeof LOAD_FOLLOWED_POSTS_COUNT, AnyObj, FollowedPostsCount>(LOAD_FOLLOWED_POSTS_COUNT)


// ===========================================================================//
// Comments                                                                   //
// ===========================================================================//

export type NormalizedCommentsResponse = {
  users: Collection<FeedUser, 'uid'>
  comments: Collection<Comment, 'uid'>
  thread: CommentsThread
  images: Collection<UserImage, 'id'>
}

export type WithOrdering<TOrdering = FeedOrdering> = {
  ordering: TOrdering
}

export type GetCommentsPayload =
  & WithPostUid
  & WithOrdering<FeedOrdering | undefined>
  & {
    highlightCommentUid?: CommentUid
  }

export const GET_COMMENTS = 'GET_COMMENTS' as const
export type GetComments = RequestAction<typeof GET_COMMENTS, GetCommentsPayload, NormalizedCommentsResponse>
export const getCommentsActionCreator =
  createRequestActionCreators<typeof GET_COMMENTS, GetCommentsPayload, NormalizedCommentsResponse>(GET_COMMENTS)

type LoadCommentThreadRequest =
  & WithCommentUid
  & WithPostUid
type LoadCommentThreadResponse =
  & NormalizedCommentsResponse
export const LOAD_COMMENT_THREAD = 'LOAD_COMMENT_THREAD' as const
export type LoadCommentThread = RequestAction<typeof LOAD_COMMENT_THREAD, LoadCommentThreadRequest, LoadCommentThreadResponse>
export const loadCommentThreadActionCreator =
  createRequestActionCreators<typeof LOAD_COMMENT_THREAD, LoadCommentThreadRequest, LoadCommentThreadResponse>(LOAD_COMMENT_THREAD)

type ReportCommentPayload = {
  uid: CommentUid
}
export const REPORT_COMMENT = 'REPORT_COMMENT' as const
type ReportComment = RequestAction<typeof REPORT_COMMENT, ReportCommentPayload, AnyObj>
export const reportCommentActionCreator =
  createRequestActionCreators<typeof REPORT_COMMENT, ReportCommentPayload, AnyObj>(REPORT_COMMENT)

type DeleteCommentRequest = {
  uid: CommentUid
  postUid: PostUid
}
type DeleteCommentResponse = {
  childrenCount: number
}
export const DELETE_COMMENT = 'DELETE_COMMENT' as const
type DeleteComment = RequestAction<typeof DELETE_COMMENT, DeleteCommentRequest, DeleteCommentResponse>
export const deleteCommentActionCreator =
  createRequestActionCreators<typeof DELETE_COMMENT, DeleteCommentRequest, DeleteCommentResponse>(DELETE_COMMENT)

type ToggleEditCommentPayload = {
  uid: CommentUid
  isEditing: boolean
}
export const TOGGLE_EDIT_COMMENT = 'TOGGLE_EDIT_COMMENT' as const
type ToggleEditComment = PayloadAction<typeof TOGGLE_EDIT_COMMENT, ToggleEditCommentPayload>
export const toggleEditCommentActionCreator = (payload: ToggleEditCommentPayload) =>
  createPayloadAction(TOGGLE_EDIT_COMMENT, payload)


type ToggleCommentRepliesPayload = {
  commentUid: CommentUid
  toShow: boolean
}
export const TOGGLE_COMMENT_REPLIES = 'TOGGLE_COMMENT_REPLIES' as const
export const toggleCommentRepliesActionCreator = (payload: ToggleCommentRepliesPayload): PayloadAction<typeof TOGGLE_COMMENT_REPLIES, ToggleCommentRepliesPayload> => createPayloadAction(TOGGLE_COMMENT_REPLIES, payload)
type ToggleCommentReplies = ReturnType<typeof toggleCommentRepliesActionCreator>


type InitCommentReplyRequest = {
  commentUid: CommentUid
}
export const INIT_COMMENT_REPLY = 'INIT_COMMENT_REPLY' as const
export const initCommentReplyActionCreator =
  (payload: InitCommentReplyRequest): PayloadAction<typeof INIT_COMMENT_REPLY, InitCommentReplyRequest> =>
    createPayloadAction(INIT_COMMENT_REPLY, payload)
type InitCommentReply = ReturnType<typeof initCommentReplyActionCreator>


type CommentReplyCancelPayload = {
  commentUid: CommentUid
}
export const COMMENT_REPLY_CANCEL = 'COMMENT_REPLY_CANCEL' as const
export const commentReplyCancelActionCreator =
  (payload: CommentReplyCancelPayload): PayloadAction<typeof COMMENT_REPLY_CANCEL, CommentReplyCancelPayload> =>
    createPayloadAction(COMMENT_REPLY_CANCEL, payload)
type CommentReplyCancel = ReturnType<typeof commentReplyCancelActionCreator>


// ===========================================================================//
// Filters                                                                    //
// ===========================================================================//

export const SELECT_FEED_FILTER = 'SELECT_FEED_FILTER' as const
type SelectFeedFilter = PayloadAction<typeof SELECT_FEED_FILTER, SelectableFeedFilter>
export const selectFeedFilterActionCreator = (payload: SelectableFeedFilter): SelectFeedFilter =>
  createPayloadAction(SELECT_FEED_FILTER, payload)

type SetShownPopupFeedFilterPayload = {
  filter: SelectableFeedFilter
  rect: ClientRect
}
export const SHOW_POPUP_FEED_FILTER = 'SHOW_POPUP_FEED_FILTER' as const
type ShowPopupFeedFilter = PayloadAction<typeof SHOW_POPUP_FEED_FILTER, SetShownPopupFeedFilterPayload>
export const showPopupFeedFilterActionCreator = (rect: ClientRect, filter: SelectableFeedFilter): ShowPopupFeedFilter =>
  createPayloadAction(SHOW_POPUP_FEED_FILTER, { filter, rect })

export const HIDE_POPUP_FEED_FILTER = 'HIDE_POPUP_FEED_FILTER' as const
type HidePopupFeedFilter = SimpleAction<typeof HIDE_POPUP_FEED_FILTER>
export const hidePopupFeedFilterActionCreator = (): HidePopupFeedFilter =>
  createAction(HIDE_POPUP_FEED_FILTER)

export const REGISTER_POPUP_FEED_FILTER_TIMEOUT = 'REGISTER_POPUP_FEED_FILTER_TIMEOUT' as const
type RegisterPopupFeedFilterTimeout = PayloadAction<typeof REGISTER_POPUP_FEED_FILTER_TIMEOUT, number>
export const registerPopupFeedFilterTimeoutActionCreator = (payload: number): RegisterPopupFeedFilterTimeout =>
  createPayloadAction(REGISTER_POPUP_FEED_FILTER_TIMEOUT, payload)

export const GET_FILTERS_STATUS = 'GET_FILTERS_STATUS' as const
export type GetFiltersStatus = RequestAction<typeof GET_FILTERS_STATUS, FeedFilterQuery[], FeedFilterStatus[]>
export const getFiltersStatusActionCreator =
  createRequestActionCreators<typeof GET_FILTERS_STATUS, FeedFilterQuery[], FeedFilterStatus[]>(GET_FILTERS_STATUS)

type ChangeSocialProfileStepPayload = {
  step: SocialProfileState['step']
  focusComposeId?: ComposeId
}
export const CHANGE_SOCIAL_PROFILE_STEP = 'CHANGE_SOCIAL_PROFILE_STEP' as const
export type ChangeSocialProfileStep = PayloadAction<typeof CHANGE_SOCIAL_PROFILE_STEP, ChangeSocialProfileStepPayload>
export const changeSocialProfileStepActionCreator = (step: SocialProfileState['step'], focusComposeId?: ComposeId) =>
  createPayloadAction(CHANGE_SOCIAL_PROFILE_STEP, { step, focusComposeId })

type GetRandomFeedRelationResponse = {
  peergroup: Peergroup
  relation: PeergroupRelation
}
export const GET_RANDOM_FEED_RELATION = 'GET_RANDOM_FEED_RELATION' as const
type GetRandomFeedRelation = RequestAction<typeof GET_RANDOM_FEED_RELATION, AnyObj, GetRandomFeedRelationResponse>
export const getRandomFeedRelationActionCreator = createRequestActionCreators<
  typeof GET_RANDOM_FEED_RELATION,
  AnyObj,
  GetRandomFeedRelationResponse
>(GET_RANDOM_FEED_RELATION)

type RandomizeFeedRelationResponse = {
  peergroup: Peergroup
  relation: PeergroupRelation
}
export const RANDOMIZE_FEED_RELATION = 'RANDOMIZE_FEED_RELATION' as const
type RandomizeFeedRelation = RequestAction<typeof RANDOMIZE_FEED_RELATION, AnyObj, RandomizeFeedRelationResponse>
export const randomizeFeedRelationActionCreator = createRequestActionCreators<
  typeof RANDOMIZE_FEED_RELATION,
  AnyObj,
  RandomizeFeedRelationResponse
>(RANDOMIZE_FEED_RELATION)

type ClickComposePayload =
  & WithComposeId
export const CLICK_COMPOSE = 'CLICK_COMPOSE' as const
export const clickComposeActionCreator = (payload: ClickComposePayload): PayloadAction<typeof CLICK_COMPOSE, ClickComposePayload> => createPayloadAction(CLICK_COMPOSE, payload)
type ClickCompose = ReturnType<typeof clickComposeActionCreator>


type FeedAction =
  | GetFeedMemberInfo
  | GetFeedAuthor
  | GetFeedTag
  | GetFeedTags
  | ToggleAuthorFollowing
  | ToggleTagFollowing
  | TogglePostFollowing
  | ToggleTagFollowing
  | ToggleCommentLike
  | TogglePostLike
  | ReportComment
  | GetPosts
  | ResetPosts
  | ReportPost
  | ReportInsight
  | DeletePost
  | DeleteComment
  | ToggleEditPost
  | ToggleEditComment
  | SelectFeedFilter
  | ShowPopupFeedFilter
  | HidePopupFeedFilter
  | RegisterPopupFeedFilterTimeout
  | LoadFollowedPostsCount
  | GetFiltersStatus
  | GetComments
  | LoadCommentThread
  | ChangeSocialProfileStep
  | GetFollowedUsers
  | ResetFollowedUsers
  | ScheduleResetFollowedUsers
  | GetFollowedTags
  | ResetFollowedTags
  | ScheduleResetFollowedTags
  | GetRandomFeedRelation
  | RandomizeFeedRelation
  | ToggleCommentReplies
  | InitCommentReply
  | CommentReplyCancel
  | ClickCompose

export default FeedAction
